import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, withCtx as _withCtx, resolveComponent as _resolveComponent, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass } from "vue"

import { computed, ref } from "vue";
import { ArtifactSchema } from "@/types";
import { getIcon } from "@/util";
import {
  artifactStore,
  permissionStore,
  selectionStore,
  subtreeStore,
  traceSaveStore,
  traceStore,
} from "@/hooks";
import {
  Typography,
  IconButton,
  PanelCard,
  TextButton,
  FlexBox,
} from "@/components/common";
import ExpansionItem from "@/components/common/display/list/ExpansionItem.vue";
import ArtifactListDisplay from "./ArtifactListDisplay.vue";


/**
 * Displays the selected node's parents and children.
 */
const __default__ = {
  name: "ArtifactContext",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const groupType = ref<"type" | "hierarchy">("type");

const displayActions = computed(() =>
  permissionStore.isAllowed("project.edit_data")
);

const artifact = computed(() => artifactStore.selectedArtifact);

const artifactId = computed(() => artifact.value?.id || "");

const relatedArtifacts = computed<Record<string, ArtifactSchema[]>>(() =>
  groupType.value === "type"
    ? (
        subtreeStore
          .getParentsAndChildren(artifactId.value)
          .map((id) => artifactStore.getArtifactById(id))
          .filter((artifact) => !!artifact) as ArtifactSchema[]
      ).reduce<Record<string, ArtifactSchema[]>>(
        (acc, artifact) => ({
          ...acc,
          [artifact.type]: [...(acc[artifact.type] || []), artifact],
        }),
        {}
      )
    : {
        Parents: subtreeStore
          .getParents(artifactId.value)
          .map((id) => artifactStore.getArtifactById(id))
          .filter((artifact) => !!artifact) as ArtifactSchema[],
        Children: subtreeStore
          .getChildren(artifactId.value)
          .map((id) => artifactStore.getArtifactById(id))
          .filter((artifact) => !!artifact) as ArtifactSchema[],
      }
);

/**
 * Determines the className of the link to another artifact.
 * @param artifactName - The artifact to select the link to.
 * @returns The className for the link.
 */
function getTraceLinkClassName(artifactName: string): string {
  const relatedArtifact = artifactStore.getArtifactByName(artifactName);

  if (!relatedArtifact || !artifact.value) return "trace-chip text-nodeDefault";

  const traceLink = traceStore.getTraceLinkByArtifacts(
    relatedArtifact.id,
    artifact.value.id,
    true
  );

  const base =
    traceLink?.traceType === "GENERATED"
      ? "trace-chip-generated text-nodeGenerated "
      : "trace-chip text-nodeDefault ";
  const unreviewed =
    traceLink?.approvalStatus === "UNREVIEWED" ? "trace-chip-unreviewed" : "";

  return base + unreviewed;
}

/**
 * Selects an artifact.
 * @param artifactName - The artifact to select.
 */
function handleArtifactClick(artifactName: string): void {
  const relatedArtifact = artifactStore.getArtifactByName(artifactName);

  if (!relatedArtifact) return;

  selectionStore.selectArtifact(relatedArtifact.id);
}

/**
 * Selects the trace link to an artifact.
 * @param artifactName - The artifact to select the link to.
 */
function handleTraceLinkClick(artifactName: string): void {
  const relatedArtifact = artifactStore.getArtifactByName(artifactName);

  if (!relatedArtifact || !artifact.value) return;

  const traceLink = traceStore.getTraceLinkByArtifacts(
    relatedArtifact.id,
    artifact.value.id,
    true
  );

  if (!traceLink) return;

  selectionStore.selectTraceLink(traceLink);
}

/**
 * Opens the create trace link panel with this artifact as the child.
 */
function handleLinkParent(): void {
  if (!artifact.value) return;

  traceSaveStore.openPanel({
    type: "source",
    artifactId: artifact.value.id,
  });
}

/**
 * Opens the create trace link panel with this artifact as the parent.
 */
function handleLinkChild(): void {
  if (!artifact.value) return;

  traceSaveStore.openPanel({
    type: "target",
    artifactId: artifact.value.id,
  });
}

return (_ctx: any,_cache: any) => {
  const _component_q_btn_dropdown = _resolveComponent("q-btn-dropdown")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(PanelCard), {
      title: "Related Context",
      collapsable: "",
      borderless: ""
    }, {
      "title-actions": _withCtx(() => [
        (displayActions.value)
          ? (_openBlock(), _createBlock(_unref(TextButton), {
              key: 0,
              text: "",
              small: "",
              label: "Parent",
              icon: "trace",
              "icon-rotate": -90,
              "data-cy": "button-artifact-link-parent",
              onClick: handleLinkParent
            }))
          : _createCommentVNode("", true),
        (displayActions.value)
          ? (_openBlock(), _createBlock(_unref(TextButton), {
              key: 1,
              text: "",
              small: "",
              label: "Child",
              icon: "trace",
              "icon-rotate": 90,
              "data-cy": "button-artifact-link-child",
              onClick: handleLinkChild
            }))
          : _createCommentVNode("", true),
        _createVNode(_component_q_btn_dropdown, {
          flat: "",
          "auto-close": "",
          dense: "",
          size: "sm",
          "dropdown-icon": "",
          icon: groupType.value === 'type' ? _unref(getIcon)('artifact') : _unref(getIcon)('trace')
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(FlexBox), { column: "" }, {
              default: _withCtx(() => [
                _createVNode(_unref(TextButton), {
                  label: "Type",
                  icon: "artifact",
                  small: "",
                  text: "",
                  block: "",
                  align: "start",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (groupType.value = 'type'))
                }),
                _createVNode(_unref(TextButton), {
                  label: "Hierarchy",
                  icon: "trace",
                  small: "",
                  block: "",
                  text: "",
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (groupType.value = 'hierarchy'))
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["icon"])
      ]),
      default: _withCtx(() => [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(Object.keys(relatedArtifacts.value), (artifactType) => {
          return (_openBlock(), _createBlock(ExpansionItem, {
            key: artifactType,
            label: artifactType,
            "default-opened": ""
          }, {
            default: _withCtx(() => [
              _createVNode(ArtifactListDisplay, {
                "data-cy": 'list-selected-' + artifactType,
                artifacts: relatedArtifacts.value[artifactType],
                "action-cols": 1,
                class: "bg-background rounded",
                "item-data-cy": 'list-selected-item-' + artifactType,
                onClick: _cache[2] || (_cache[2] = ($event: any) => (handleArtifactClick($event.name)))
              }, {
                actions: _withCtx(({ artifact: parent }) => [
                  _createVNode(_unref(IconButton), {
                    outline: "",
                    flat: false,
                    icon: "trace",
                    tooltip: "View Trace Link",
                    "data-cy": "button-selected-parent-link",
                    class: _normalizeClass(getTraceLinkClassName(parent.name)),
                    onClick: ($event: any) => (handleTraceLinkClick(parent.name))
                  }, null, 8, ["class", "onClick"])
                ]),
                _: 2
              }, 1032, ["data-cy", "artifacts", "item-data-cy"])
            ]),
            _: 2
          }, 1032, ["label"]))
        }), 128)),
        (Object.keys(relatedArtifacts.value).length === 0)
          ? (_openBlock(), _createBlock(_unref(Typography), {
              key: 0,
              l: "1",
              variant: "caption",
              value: "There are no parent artifacts."
            }))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ]))
}
}

})